import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useTurnstile } from '../lib/useTurnstile';
import { AuthShell } from '../components/AuthShell';

const RESEND_SECONDS = 30;

const emailSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

const codeSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/u, 'Enter the 6-digit code from your email'),
});

type EmailForm = z.infer<typeof emailSchema>;
type CodeForm = z.infer<typeof codeSchema>;

export const Route = createFileRoute('/otp')({
  component: OtpPage,
});

function OtpPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const setToken = useAuthStore((s) => s.setToken);
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [email, setEmail] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [resending, setResending] = useState(false);
  // Force-remount the Turnstile widget slot. Bumped whenever a fresh token
  // is required (post-send, post-resend). The hook's `ref` callback fires
  // with null on unmount (clearing the token + widget id) and with the new
  // element on mount, producing a clean single-use token each time.
  const [turnstileMountKey, setTurnstileMountKey] = useState(0);

  const { ref: turnstileRef, token: turnstileToken, reset: resetTurnstile } =
    useTurnstile();

  const emailForm = useForm<EmailForm>({ resolver: zodResolver(emailSchema) });
  const codeForm = useForm<CodeForm>({ resolver: zodResolver(codeSchema) });

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setInterval(() => {
      setResendIn((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendIn]);

  const onRequest = async (values: EmailForm) => {
    setServerError(null);
    if (!turnstileToken) {
      setServerError('Please complete the bot check above.');
      return;
    }
    try {
      await api.requestOtp({ email: values.email, turnstileToken });
    } catch (err) {
      resetTurnstile();
      // Don't disclose existence. Only block on hard transport errors.
      if (err instanceof ApiError && err.status >= 500) {
        setServerError(err.message);
        return;
      }
      if (!(err instanceof ApiError)) {
        setServerError('Something went wrong. Please try again.');
        return;
      }
      // Silently advance for 4xx — preserve user enumeration defense.
    }
    setEmail(values.email);
    setStep('verify');
    setResendIn(RESEND_SECONDS);
    // The token we just used is now spent server-side. Clear it so the next
    // requestOtp (resend) is forced to wait for a fresh token from a freshly
    // mounted widget.
    resetTurnstile();
    setTurnstileMountKey((k) => k + 1);
  };

  const onVerify = async (values: CodeForm) => {
    setServerError(null);
    try {
      const result = await api.verifyOtp({ email, code: values.code });
      setToken(result.sessionToken);
      const me = await api.me();
      setUser(me);
      void navigate({ to: '/dashboard' });
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  const onResend = async () => {
    if (resendIn > 0 || resending) return;
    setServerError(null);
    // A Turnstile token is single-use server-side, so the token captured
    // during step 1 has already been spent. Require a fresh one before
    // issuing the resend request.
    if (!turnstileToken) {
      setServerError('Please complete the bot check below before resending.');
      return;
    }
    setResending(true);
    try {
      await api.requestOtp({ email, turnstileToken });
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      if (err instanceof ApiError && err.status >= 500) {
        setServerError(err.message);
      }
      // For 4xx, silently reset the timer — don't leak whether the email exists.
      setResendIn(RESEND_SECONDS);
    } finally {
      // Burn the token we just used and force the widget to remount so the
      // next resend (or retry) is gated on a fresh challenge solve.
      resetTurnstile();
      setTurnstileMountKey((k) => k + 1);
      setResending(false);
    }
  };

  return (
    <AuthShell
      tagline="Sign in with a one-time code."
      taglineSub="No password needed — we’ll email a 6-digit code."
    >
      <p className="text-sm text-[color:var(--color-text-muted)]">
        Staff use email and password.{' '}
        <Link to="/login" className="text-gold-700 underline">
          Sign in with password →
        </Link>
      </p>

      {step === 'request' ? (
        <>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">
            Get a sign-in code
          </h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
            Enter your email and we’ll send you a 6-digit code.
          </p>

          <form
            onSubmit={emailForm.handleSubmit(onRequest)}
            className="mt-8 space-y-5"
            noValidate
          >
            <div>
              <label htmlFor="otp-email" className="block text-sm font-medium">
                Email
              </label>
              <input
                id="otp-email"
                type="email"
                autoComplete="email"
                {...emailForm.register('email')}
                className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
                aria-invalid={emailForm.formState.errors.email ? 'true' : undefined}
                aria-describedby={
                  emailForm.formState.errors.email ? 'otp-email-error' : undefined
                }
              />
              {emailForm.formState.errors.email && (
                <p id="otp-email-error" className="mt-1 text-sm text-danger">
                  {emailForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div key={`turnstile-request-${turnstileMountKey}`} ref={turnstileRef} role="group" aria-label="Bot check" />

            {serverError && (
              <p role="alert" className="text-sm text-danger">
                {serverError}
              </p>
            )}

            <button
              type="submit"
              disabled={emailForm.formState.isSubmitting || !turnstileToken}
              className="w-full inline-flex items-center justify-center rounded-md bg-ink-900 text-parchment-50 px-5 py-2.5 text-sm font-semibold hover:bg-ink-800 disabled:opacity-60"
            >
              {emailForm.formState.isSubmitting ? 'Sending…' : 'Send code'}
            </button>
          </form>
        </>
      ) : (
        <>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">
            Enter your code
          </h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
            If an account exists for{' '}
            <span className="font-semibold text-[color:var(--color-text)]">{email}</span>{' '}
            we’ve sent a 6-digit code. It expires in 10 minutes.
          </p>

          <form
            onSubmit={codeForm.handleSubmit(onVerify)}
            className="mt-8 space-y-5"
            noValidate
          >
            <div>
              <label htmlFor="otp-code" className="block text-sm font-medium">
                6-digit code
              </label>
              <input
                id="otp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                {...codeForm.register('code')}
                className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2 font-mono tracking-[0.4em] text-center text-lg"
                aria-invalid={codeForm.formState.errors.code ? 'true' : undefined}
                aria-describedby={
                  codeForm.formState.errors.code ? 'otp-code-error' : undefined
                }
              />
              {codeForm.formState.errors.code && (
                <p id="otp-code-error" className="mt-1 text-sm text-danger">
                  {codeForm.formState.errors.code.message}
                </p>
              )}
            </div>

            {serverError && (
              <p role="alert" className="text-sm text-danger">
                {serverError}
              </p>
            )}

            <button
              type="submit"
              disabled={codeForm.formState.isSubmitting}
              className="w-full inline-flex items-center justify-center rounded-md bg-ink-900 text-parchment-50 px-5 py-2.5 text-sm font-semibold hover:bg-ink-800 disabled:opacity-60"
            >
              {codeForm.formState.isSubmitting ? 'Verifying…' : 'Sign in'}
            </button>

            {/*
              A fresh Turnstile widget lives on step 2 so the "Resend" action
              has a single-use token ready when the cooldown expires. With the
              test sitekey this solves invisibly; in production the user sees
              the challenge again before resending.
            */}
            <div
              key={`turnstile-resend-${turnstileMountKey}`}
              ref={turnstileRef}
              role="group" aria-label="Bot check for resend"
            />

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={onResend}
                disabled={resendIn > 0 || resending || !turnstileToken}
                className="text-gold-700 underline disabled:text-[color:var(--color-text-muted)] disabled:no-underline disabled:cursor-not-allowed"
              >
                {resending
                  ? 'Sending…'
                  : resendIn > 0
                    ? `Didn’t get a code? Try again in ${resendIn}s`
                    : !turnstileToken
                      ? 'Complete the bot check to resend'
                      : 'Didn’t get a code? Resend'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('request');
                  setServerError(null);
                  setResendIn(0);
                  codeForm.reset();
                  resetTurnstile();
                  setTurnstileMountKey((k) => k + 1);
                }}
                className="text-[color:var(--color-text-muted)] underline"
              >
                Use a different email
              </button>
            </div>
          </form>
        </>
      )}
    </AuthShell>
  );
}
